import React, { useState } from 'react';
import { formatDate, useAdmin } from '../context';
import { PanelReport } from '../types';
import { Empty, Segmented, ViewHeader } from '../ui';

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

type ReportFilter = 'open' | 'all';

const ReportsViewInner: React.FC<{ reports: PanelReport[] }> = ({ reports }) => {
  const { send, toast } = useAdmin();
  const [filter, setFilter] = useState<ReportFilter>('open');
  const open = reports.filter((r) => r.status === 'open');
  const shown = (filter === 'open' ? open : reports).slice().sort((a, b) => b.createdAt - a.createdAt);

  // The reportAction relays answer nothing but the refreshed list: confirm locally
  const run = (action: string, id: string, done: string) => {
    send({ action, target: id });
    toast(done, { kind: 'ok' });
  };

  return (
    <div className="admin-panel__view">
      <ViewHeader title="Signalements" subtitle={`${open.length} en attente, ${reports.length} au total`}>
        <button type="button" className="admin-panel__button" onClick={() => send({ action: 'reportsRefresh' })}>Actualiser</button>
      </ViewHeader>
      <div className="admin-panel__toolbar">
        <Segmented<ReportFilter>
          label="Filtrer les signalements"
          value={filter}
          onChange={setFilter}
          options={[
            { id: 'open', label: 'En attente', count: open.length },
            { id: 'all', label: 'Tous', count: reports.length },
          ]}
        />
      </div>
      <div className="admin-panel__scroll">
        {shown.length === 0 ? (
          <Empty title={filter === 'open' ? 'Aucun signalement en attente' : 'Aucun signalement'} text="Les nouveaux signalements apparaissent ici après une actualisation." />
        ) : (
          <ul className="admin-panel__cards admin-panel__cards--reports">
            {shown.map((r) => (
              <li key={r.id} className={'admin-panel__card admin-panel__report' + (r.status !== 'open' ? ' admin-panel__report--done' : '')}>
                <div className="admin-panel__report-head">
                  <div className="admin-panel__card-title">
                    {r.reporterName}
                    <span className={'admin-panel__tag' + (r.status === 'open' ? ' admin-panel__tag--warn' : '')}>
                      {r.status === 'open' ? 'En attente' : r.status === 'resolved' ? 'Résolu' : 'Rejeté'}
                    </span>
                  </div>
                  <div className="admin-panel__cell--muted">{formatDate(r.createdAt)}</div>
                </div>
                <p className="admin-panel__report-text">{r.text}</p>
                <div className="admin-panel__report-foot">
                  <div className="admin-panel__cell--muted admin-panel__report-where">
                    {r.cellOrWorldDesc ? `Lieu : ${r.cellOrWorldDesc}` : 'Lieu inconnu'}
                    {r.status !== 'open' ? ` - traité par ${r.resolvedByName || 'inconnu'}${r.resolvedAt ? `, ${formatDate(r.resolvedAt)}` : ''}` : ''}
                  </div>
                  <div className="admin-panel__btnrow">
                    <button type="button" className="admin-panel__button admin-panel__button--sm" onClick={() => run('reportTeleport', r.id, 'Téléportation vers le signalement...')}>
                      Aller sur place
                    </button>
                    {r.status === 'open' ? (
                      <>
                        <button type="button" className="admin-panel__button admin-panel__button--primary admin-panel__button--sm" onClick={() => run('reportResolve', r.id, 'Signalement résolu.')}>
                          Résoudre
                        </button>
                        <button type="button" className="admin-panel__button admin-panel__button--danger admin-panel__button--sm" onClick={() => run('reportDismiss', r.id, 'Signalement rejeté.')}>
                          Rejeter
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Broadcast
// ---------------------------------------------------------------------------

const DURATIONS = [5, 10, 20, 30, 60];
const TEMPLATES = [
  'Le serveur redémarre dans 5 minutes, merci de vous mettre en sécurité.',
  'Un événement commence bientôt, restez attentifs.',
  'Merci de respecter les règles du jeu de rôle.',
];
const MAX_TEXT = 300;

const BroadcastViewInner: React.FC = () => {
  const { act, confirm } = useAdmin();
  const [text, setText] = useState('');
  const [durationSec, setDurationSec] = useState(10);
  const trimmed = text.trim();

  const send = () => {
    if (!trimmed) return;
    confirm({
      title: 'Envoyer cette annonce à tous les joueurs ?',
      body: <span className="admin-panel__quote">{trimmed}</span>,
      confirmLabel: 'Envoyer',
      onConfirm: () => {
        act('broadcastMessage', { text: trimmed, durationSec }, 'Annonce');
        setText('');
      },
    });
  };

  return (
    <div className="admin-panel__view admin-panel__view--scroll">
      <ViewHeader title="Annonces" subtitle="Un bandeau s'affiche à l'écran de tous les joueurs en ligne." />
      <div className="admin-panel__card admin-panel__card--flat admin-panel__form">
        <label className="admin-panel__field-label" htmlFor="ap-bc-text">Message</label>
        <textarea
          id="ap-bc-text"
          className="admin-panel__input admin-panel__textarea"
          value={text}
          maxLength={MAX_TEXT}
          placeholder="Le serveur redémarre dans 5 minutes..."
          onChange={(e) => setText(e.target.value)}
        />
        <div className="admin-panel__form-foot">
          <span className="admin-panel__cell--muted">{text.length} / {MAX_TEXT}</span>
        </div>

        <div className="admin-panel__field-label">Durée à l'écran</div>
        <div className="admin-panel__chips">
          {DURATIONS.map((d) => (
            <button key={d} type="button" aria-pressed={d === durationSec} className={'admin-panel__chip' + (d === durationSec ? ' admin-panel__chip--active' : '')} onClick={() => setDurationSec(d)}>
              {d} s
            </button>
          ))}
        </div>

        <div className="admin-panel__field-label">Modèles</div>
        <ul className="admin-panel__templates">
          {TEMPLATES.map((t) => (
            <li key={t}>
              <button type="button" className="admin-panel__template" onClick={() => setText(t)}>{t}</button>
            </li>
          ))}
        </ul>

        <div className="admin-panel__form-actions">
          <button type="button" className="admin-panel__button admin-panel__button--primary" disabled={!trimmed} onClick={send}>
            Envoyer à tous
          </button>
        </div>
      </div>
    </div>
  );
};

export const ReportsView = React.memo(ReportsViewInner);
export const BroadcastView = React.memo(BroadcastViewInner);
